import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Badge,
  IconButton,
  Icon,
  Divider,
  FormControl,
  FormLabel,
  Box,
  useColorModeValue,
  useToast,
  Heading,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { MdDelete, MdAdd } from 'react-icons/md';
import { adminApi } from '../../services/adminApi';
import { useAdminTenants } from '../../hooks/useAdminTenants';
import type { AdminUser, AdminUserMembership, TenantRole, AddMembershipInput, UpdateMembershipInput } from '../../types/admin';
import { toUserMessage } from '../../lib/apiError';

interface AdminManageMembershipsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser;
  onUpdated: (user: AdminUser) => void;
}

const ROLE_OPTIONS: { value: TenantRole; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'TENANT_ADMIN', label: 'Admin' },
];

export const AdminManageMembershipsModal: React.FC<AdminManageMembershipsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdated,
}) => {
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const { tenants } = useAdminTenants({ limit: 100 });

  const [currentUser, setCurrentUser] = useState<AdminUser>(user);
  const [addTenantId, setAddTenantId] = useState('');
  const [addRole, setAddRole] = useState<TenantRole>('USER');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Sync local user when modal opens or prop changes
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      setAddTenantId('');
      setAddRole('USER');
    }
  }, [isOpen]);

  const membershipTenantIds = new Set(currentUser.memberships?.map((m) => m.tenantId) ?? []);
  const availableTenants = tenants.filter((t) => !membershipTenantIds.has(t.id) && t.isActive);

  const applyUpdate = (updated: AdminUser) => {
    setCurrentUser(updated);
    onUpdated(updated);
  };

  const handleAdd = async () => {
    if (!addTenantId) return;
    setAdding(true);
    try {
      const data: AddMembershipInput = { tenantId: addTenantId, tenantRole: addRole };
      const updated = await adminApi.users.addMembership(currentUser.id, data);
      applyUpdate(updated);
      setAddTenantId('');
      setAddRole('USER');
      toast({ title: 'Added to tenant', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: toUserMessage(err, "We couldn't add the membership. Please try again."), status: 'error', duration: 3000 });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (membership: AdminUserMembership) => {
    setRemovingId(membership.tenantId);
    try {
      const updated = await adminApi.users.removeMembership(currentUser.id, membership.tenantId);
      applyUpdate(updated);
      toast({ title: `Removed from ${membership.tenantName}`, status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: toUserMessage(err, "We couldn't remove the membership. Please try again."), status: 'error', duration: 3000 });
    } finally {
      setRemovingId(null);
    }
  };

  const handleRoleChange = async (membership: AdminUserMembership, newRole: TenantRole) => {
    setUpdatingId(membership.tenantId);
    try {
      const data: UpdateMembershipInput = { tenantRole: newRole };
      const updated = await adminApi.users.updateMembership(currentUser.id, membership.tenantId, data);
      applyUpdate(updated);
      toast({ title: 'Role updated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: toUserMessage(err, "We couldn't update that role. Please try again."), status: 'error', duration: 3000 });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Organizations — {currentUser.displayName || currentUser.email}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Current memberships */}
            <Box>
              <Heading size="sm" mb={3}>Current memberships ({currentUser.memberships?.length ?? 0})</Heading>
              {!currentUser.memberships?.length ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  No memberships yet.
                </Alert>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Organization</Th>
                      <Th>Role</Th>
                      <Th>Owner</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentUser.memberships.map((m) => (
                      <Tr key={m.tenantId}>
                        <Td>
                          <Text fontWeight="medium">{m.tenantName}</Text>
                          <Text fontSize="xs" color={mutedColor}>{m.tenantSlug}</Text>
                        </Td>
                        <Td>
                          <Select
                            size="xs"
                            value={m.tenantRole}
                            isDisabled={updatingId === m.tenantId}
                            onChange={(e) => handleRoleChange(m, e.target.value as TenantRole)}
                            w="120px"
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Select>
                        </Td>
                        <Td>
                          {m.isOwner ? <Badge colorScheme="purple">Owner</Badge> : <Text color={mutedColor} fontSize="sm">—</Text>}
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Remove from tenant"
                            icon={<Icon as={MdDelete} />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            isLoading={removingId === m.tenantId}
                            onClick={() => handleRemove(m)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>

            <Divider />

            {/* Add to tenant */}
            <Box>
              <Heading size="sm" mb={3}>Add to organization</Heading>
              {availableTenants.length === 0 ? (
                <Text color={mutedColor} fontSize="sm">No available organizations to add.</Text>
              ) : (
                <HStack align="flex-end" spacing={3}>
                  <FormControl flex={2}>
                    <FormLabel fontSize="sm">Organization</FormLabel>
                    <Select
                      placeholder="Select organization..."
                      value={addTenantId}
                      onChange={(e) => setAddTenantId(e.target.value)}
                      size="sm"
                    >
                      {availableTenants.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">Role</FormLabel>
                    <Select
                      value={addRole}
                      onChange={(e) => setAddRole(e.target.value as TenantRole)}
                      size="sm"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    leftIcon={<Icon as={MdAdd} />}
                    colorScheme="blue"
                    size="sm"
                    isDisabled={!addTenantId}
                    isLoading={adding}
                    onClick={handleAdd}
                  >
                    Add
                  </Button>
                </HStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminManageMembershipsModal;
